/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import { useDebounce } from '../hooks';
import { ProdutosService } from '../services/api/Venda/ProdutosService';
import { pdvScale } from '../../pages/PDV/pdvScale';

export type TAutoCompleteOption = {
  idprod: string;
  label: string;
  descricao: string;
  marca: string;
  vencimento: boolean;
  referencia: string;
  codinterno?: number;
  precoSaida: number;
  precovenda: number;
  idproduto: string;
  un: string;
  // Linha sintética adicionada só pro rodapé "todos os N itens já foram
  // carregados" (ver FOOTER_TODOS_CARREGADOS abaixo) — nunca vem do backend,
  // nunca é selecionável.
  _footer?: boolean;
};

// Adicionada ao fim das opções quando a busca já carregou todos os
// resultados (opcoes.length >= totalCount) — deixa explícito que chegou no
// fim da lista, em vez do operador só parar de conseguir descer sem saber
// se é fim de lista ou trava. `getOptionDisabled` (ver Autocomplete abaixo)
// impede que ela seja navegável/selecionável.
const FOOTER_TODOS_CARREGADOS: TAutoCompleteOption = {
  idprod: '__footer_todos_carregados__',
  label: '',
  descricao: '',
  marca: '',
  vencimento: false,
  referencia: '',
  precoSaida: 0,
  precovenda: 0,
  idproduto: '',
  un: '',
  _footer: true,
};

// Mesmas opções de campo de busca já usadas na tela de venda (FrontSIAC —
// DialogVenda.tsx): descrição, referência ou código interno. O front do PDV
// escolhe uma delas (prop `filtro`) e passa pro backend como `tipo` —
// ver ProdutosProvaider.getAll, que só reconhece esses três valores (mais
// 'codbarra', usado à parte pelo fluxo de leitor de código de barras/Enter
// abaixo, não por este seletor).
export type TTipoBuscaProduto = 'descricao' | 'referencia' | 'codinterno';

// Quantos produtos buscar por página. Ao chegar no último item carregado e
// apertar seta pra baixo (ver onHighlightChange), busca a próxima leva com o
// mesmo tamanho — em vez de trazer tudo de uma vez ou obrigar o operador a
// refinar a busca pra encontrar itens fora das primeiras posições.
const TAMANHO_PAGINA = 8;

interface AutoCompleteTheme {
  inputBackground: string;
  dropdownBackground: string;
  dropdownHover: string;
  borderColor: string;
  textColor: string;
  placeholderColor: string;
  // Cor do texto das opções na lista suspensa — separada de `textColor`
  // (que é a cor do que o operador digita no campo) porque o fundo da
  // lista agora é claro (branco tonalizado com o tema) mesmo quando o
  // campo em si fica sobre uma barra escura; sem essa separação, o texto
  // das opções herdava `textColor` e ficava ilegível (claro sobre claro).
  // Cai de volta em `textColor` se não for passada, pra não quebrar quem
  // já usa esse componente sem essa prop.
  dropdownTextColor?: string;
}

interface IAutoCompleteProdutoProps {
  inputRef?: React.RefObject<HTMLInputElement>;
  themeColors?: AutoCompleteTheme;
  isExternalLoading?: boolean;
  // Bloqueia o campo por completo (ex.: caixa fechado no PDV) — diferente de
  // isExternalLoading, que é só um estado passageiro de carregamento.
  disabled?: boolean;
  // 'card' (padrão): comportamento histórico — fundo/borda próprios e cantos
  // arredondados só em cima, pensado pra ficar "grudado" num card branco
  // logo abaixo. 'bar': sem fundo/borda/radius próprios — herda tudo do
  // container pai (usado na barra de lançamento full-width do PDV).
  variant?: 'card' | 'bar';
  placeholder?: string;
  filtro?: TTipoBuscaProduto;
  // 'inicio': só o começo do texto — mais rápido/previsível pro operador
  // digitando durante a venda. 'contem' (padrão): o termo em qualquer parte
  // do texto, igual sempre foi.
  modoBusca?: 'inicio' | 'contem';
  value?: TAutoCompleteOption | null;
  inputValue?: string;
  onBarcodeFound?: (produto: any) => void;
  onBarcodeNotFound?: (codigo: string) => void;
  // Chamado quando um Enter chega enquanto a busca do código anterior ainda
  // está em andamento (ver buscandoCodigoRef abaixo) — esse Enter é
  // DESCARTADO (não enfileirado), então sem isso o operador não tinha
  // nenhum feedback de que o segundo bipe/Enter não fez nada.
  onBarcodeBusy?: () => void;
  onBarcodeEnter?: (codigo: string) => void;
  pVenda?: (valor: number | undefined) => void;
  onInputChange?: (event: React.SyntheticEvent, value: string) => void;
  onChange?: (value: TAutoCompleteOption | null) => void;
  onSelect: (item: {idprod: string; descricao: string; precovenda: number; qtds?: number; un: string; referencia: string; marca: string }) => void;
  autoClear?: boolean;
  initialSearchValue?: string;
  onQuantityDetected?: (quantidade: number) => void;
  autoSearchOnEnter?: boolean; // Nova prop para controlar busca no Enter
  onRemoveByNumber?: (numero: number) => void; // Digite -N e Enter para remover o item nº N do carrinho
  // false (padrão): mantém o nome do produto escrito no campo depois de
  // selecionar (comportamento de sempre — usado no modo edição, onde o
  // operador ainda vai conferir/ajustar qtd/preço antes de confirmar).
  // true: limpa o campo na hora em vez de escrever o nome (modo rápido —
  // ver onSelect/onBarcodeFound em Pdv.tsx, que já cuidam de mostrar o
  // nome do último item lançado no header em vez do campo).
  limparAoSelecionar?: boolean;
}

export interface AutoCompleteProdutopdvHandle {
  setSearchValue: (value: string) => void;
  clearSearch: () => void;
  focus: () => void;
}

// Busca um produto por código digitado (leitor de código de barras OU
// código interno digitado por completo + Enter) — tenta primeiro como
// código de barras (endpoint dedicado, busca exata) e só se não achar tenta
// como código interno (getAll com tipo='codinterno'; a busca por tipo é
// "começa com"/"contém", então confere igualdade exata com o que foi
// digitado antes de aceitar, senão "12" batia com "120", "125" etc.).
// Retorno normalizado pro mesmo formato que ProdutosService.getByean
// devolve (codbarraentrada em vez de ean), pra quem usa onBarcodeFound não
// precisar saber por qual dos dois caminhos o produto foi encontrado.
const buscarProdutoPorCodigo = async (codigo: string): Promise<any | null> => {
  const porEan = await ProdutosService.getByean(codigo);
  if (!(porEan instanceof Error) && porEan?.idproduto) {
    return porEan;
  }

  const porCodInterno = await ProdutosService.getAll(1, codigo, 'codinterno', 5, 'inicio');
  if (!(porCodInterno instanceof Error)) {
    const encontrado = porCodInterno.data.find((p) => String(p.codinterno) === codigo);
    if (encontrado) {
      return {
        idproduto: encontrado.idproduto,
        descricao: encontrado.descricao,
        pvenda: encontrado.pvenda ?? encontrado.preco,
        un: encontrado.un,
        referencia: encontrado.referencia,
        marca: encontrado.marca,
        codbarraentrada: encontrado.ean,
      };
    }
  }

  return null;
};

export const AutoCompleteProdutopdv = forwardRef<AutoCompleteProdutopdvHandle, IAutoCompleteProdutoProps>(
  (
    {
      inputRef,
      themeColors,
      isExternalLoading = false,
      disabled = false,
      variant = 'card',
      placeholder = 'Digite o nome do produto ou código de barras (Enter)',
      filtro = 'descricao',
      modoBusca = 'contem',
      value,
      inputValue,
      onBarcodeEnter,
      onBarcodeFound,
      onBarcodeNotFound,
      onBarcodeBusy,
      onInputChange,
      onChange,
      onSelect,
      autoClear = false,
      initialSearchValue = '',
      onQuantityDetected,
      autoSearchOnEnter = true, // Padrão: true
      onRemoveByNumber,
      limparAoSelecionar = false,
    },
    ref
  ) => {
    const { debounce } = useDebounce();
    const [opcoes, setOpcoes] = useState<TAutoCompleteOption[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    // Página já carregada e total de registros que a busca atual tem no
    // servidor — junto dão pra saber se ainda há mais itens pra buscar
    // quando o operador chegar no fim da lista carregada (ver
    // handleHighlightChange/carregarProximaPagina abaixo).
    const [pagina, setPagina] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [internalInputValue, setInternalInputValue] = useState(initialSearchValue);
    const [internalValue, setInternalValue] = useState<TAutoCompleteOption | null>(null);
    // Guarda contra resposta de uma busca antiga chegando depois de uma mais
    // nova (o operador já digitou de novo) e sobrescrevendo o resultado
    // certo — mesma técnica usada em DialogVenda.tsx no FrontSIAC.
    const requestIdRef = useRef(0);
    const carregandoMaisRef = useRef(false);
    // Última seta pressionada — só busca a próxima página quando o
    // destaque chega no último item DESCENDO (seta pra baixo); com wrap
    // (MUI volta pro último item se apertar seta pra cima no primeiro),
    // handleHighlightChange também dispara nesse caso, e sem essa guarda
    // buscava mais produtos indevidamente.
    const ultimaSetaRef = useRef<'up' | 'down' | null>(null);
    // Depois de selecionar um produto, o nome dele fica escrito no campo
    // (ver handleChange) — como isso muda `currentInputValue` por baixo,
    // dispararia o useEffect de busca abaixo de novo, reabrindo a listagem
    // com resultados pro nome que acabou de ser escolhido. Essa ref suprime
    // só essa PRÓXIMA rodada da busca; qualquer digitação de verdade depois
    // disso volta a buscar normalmente.
    const acabouDeSelecionarRef = useRef(false);

    // Marca a supressão acima e agenda um reset de segurança: se o texto
    // escolhido já for exatamente igual ao que estava no campo (ex.: Enter
    // num match único, ver bateComOpcao), o React nem dispara o useEffect
    // de busca (o valor não mudou), e a ref ficaria travada em `true` pra
    // sempre, suprimindo a PRÓXIMA pesquisa de verdade sem motivo. Digitar
    // é bem mais lento que um setTimeout(0), então isso nunca chega a
    // suprimir uma busca real.
    const marcarSelecaoRecente = () => {
      acabouDeSelecionarRef.current = true;
      setTimeout(() => { acabouDeSelecionarRef.current = false; }, 0);
    };

    const isControlled = inputValue !== undefined;
    const currentInputValue = isControlled ? inputValue : internalInputValue;
    const theme = themeColors ?? {
      inputBackground: `
    linear-gradient(180deg, #0EA5E9 0%, #0369A1 100%)
  `,
      dropdownBackground: `
    linear-gradient(180deg, #0EA5E9 0%, #0369A1 100%)
  `,
      dropdownHover: 'rgba(255,255,255,0.15)',
      borderColor: 'rgba(255,255,255,0.25)',
      textColor: '#fff',
      placeholderColor: 'rgba(255,255,255,0.75)',
    };

    useImperativeHandle(ref, () => ({
      setSearchValue: (value: string) => {
        if (!isControlled) setInternalInputValue(value);
        else if (onInputChange) onInputChange({} as React.SyntheticEvent, value);
      },
      clearSearch: () => {
        if (!isControlled) setInternalInputValue('');
        else if (onInputChange) onInputChange({} as React.SyntheticEvent, '');
        setInternalValue(null);
        setOpcoes([]);
      },
      focus: () => {
        inputRef?.current?.focus();
      },
    }));

    // Efeito para focar o campo automaticamente
    useEffect(() => {
      const timeoutId = setTimeout(() => {
        inputRef?.current?.focus();
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }, []);

    const mapProduto = (produto: any): TAutoCompleteOption => ({
      // Identidade da LINHA na listagem, não do produto: na busca por
      // referência, um mesmo produto pode ter várias referências cadastradas
      // (várias linhas de `referencia` apontando pro mesmo idproduto) — usar
      // idproduto aqui fazia várias opções da lista compartilharem o mesmo
      // idprod, e a navegação por seta (handleHighlightChange, mais abaixo,
      // que localiza o item atual com `opcoes.findIndex(idprod ===)`) achava
      // sempre a PRIMEIRA ocorrência daquele idproduto, pulando/travando ao
      // navegar por itens do mesmo produto. idref é único por linha de
      // referência; só cai pra idproduto quando não há referência (produto
      // sem nenhuma cadastrada). O idproduto de verdade pro carrinho continua
      // vindo do campo `idproduto` abaixo, nunca deste.
      idprod: produto.idref || produto.idproduto,
      // Código interno sempre primeiro, depois a descrição (a pedido) — a
      // referência só entra no fim, e só no modo de busca por referência
      // (senão a maioria dos produtos, sem referência, ficaria com um " - "
      // sobrando no final).
      label: `${produto.codinterno ? `${produto.codinterno} - ` : ''}${produto.descricao}${filtro === 'referencia' && produto.referencia ? ` - ${produto.referencia}` : ''}`,
      referencia: produto.referencia,
      codinterno: produto.codinterno,
      marca: produto.marca,
      descricao: produto.descricao,
      vencimento: produto.vencimento ?? false,
      precoSaida: produto.precosaida ?? produto.preco ?? 0,
      precovenda: produto.pvenda ?? produto.preco ?? 0,
      idproduto: produto.idproduto,
      un: produto.un,
    });

    useEffect(() => {
      if (acabouDeSelecionarRef.current) {
        acabouDeSelecionarRef.current = false;
        return;
      }

      const query = currentInputValue?.trim();

      // Se estiver vazio, limpa as opções
      if (!query || query.length === 0) {
        setOpcoes([]);
        setIsLoading(false);
        setTotalCount(0);
        return;
      }

      // Prefixo "+" — quantidade (ex.: "+5", só resolvida no Enter, ver
      // onKeyDown mais abaixo), não é termo de busca nenhum: não faz
      // consulta, só limpa a listagem enquanto o operador ainda está
      // digitando o número.
      if (query.startsWith('+')) {
        setOpcoes([]);
        setIsLoading(false);
        setTotalCount(0);
        return;
      }

      // Prefixo "*" — busca por código interno (ex.: "*123"), independente
      // do modo de busca selecionado no momento (descrição/referência). O
      // "*" sozinho (nada digitado depois ainda) só limpa a listagem — não
      // dá pra buscar um código vazio. Diferente do "+5" acima, aqui É a
      // listagem ao vivo enquanto digita (não espera o Enter) — escolher um
      // item dela, por clique/seta, vai pro carrinho normalmente.
      if (query.startsWith('*')) {
        // Só o número depois do "*" vai pra busca — o "*" em si nunca é
        // enviado (nem faria sentido, o backend não conhece esse símbolo).
        const codigoInterno = query.slice(1).trim();
        if (!codigoInterno) {
          setOpcoes([]);
          setIsLoading(false);
          setTotalCount(0);
          return;
        }

        debounce(() => {
          const requestId = ++requestIdRef.current;
          setIsLoading(true);

          // Sempre 'contem' aqui, ignorando o modoBusca selecionado na tela
          // (que é 'inicio' por padrão) — "*56" deve achar qualquer código
          // interno que TENHA 56 em algum lugar (ex.: 1256, 5678), não só o
          // que começa com 56. É esse `modoBusca` (herdado) que fazia o
          // "*56" não encontrar nada.
          ProdutosService.getAll(1, codigoInterno, 'codinterno', TAMANHO_PAGINA, 'contem')
            .then((result) => {
              if (requestId !== requestIdRef.current) return; // resposta antiga, ignora
              setIsLoading(false);

              if (result instanceof Error) {
                console.error(result.message);
                setOpcoes([]);
                setTotalCount(0);
                return;
              }

              setOpcoes(result.data.map(mapProduto));
              setTotalCount(result.totalCount);
              setPagina(1);
            });
        });
        return;
      }

      // Busca por texto — mesmo com números puros (ex.: "500"), busca
      // normalmente como se fosse texto (o código de barras só é tentado no
      // Enter, ver onKeyDown mais abaixo — a listagem ao vivo aqui nunca
      // trava esperando o Enter). Sempre reinicia da página 1 (troca de
      // termo, de campo de busca ou de modo de busca invalida o que estava
      // carregado; "carregar mais" abaixo é que avança as páginas seguintes).
      debounce(() => {
        const requestId = ++requestIdRef.current;
        setIsLoading(true);

        ProdutosService.getAll(1, query, filtro, TAMANHO_PAGINA, modoBusca)
          .then((result) => {
            if (requestId !== requestIdRef.current) return; // resposta antiga, ignora
            setIsLoading(false);

            if (result instanceof Error) {
              console.error(result.message);
              setOpcoes([]);
              setTotalCount(0);
              return;
            }

            setOpcoes(result.data.map(mapProduto));
            setTotalCount(result.totalCount);
            setPagina(1);
          });
      });
    }, [currentInputValue, filtro, modoBusca]);

    // Busca a próxima leva de resultados e ANEXA ao final da lista já
    // carregada (não substitui) — chamado ao navegar com a seta pra baixo e
    // chegar no último item carregado (ver onHighlightChange no Autocomplete
    // abaixo), pra continuar "rolando" pelos resultados sem precisar
    // refinar a busca nem trazer tudo de uma vez.
    const carregarProximaPagina = () => {
      const queryDigitada = currentInputValue?.trim();
      if (!queryDigitada || carregandoMaisRef.current || opcoes.length >= totalCount) return;

      // Mesma regra do "*código" na busca ao vivo (ver useEffect acima) —
      // carregar mais páginas de um "*123" precisa continuar buscando por
      // código interno, não pelo filtro (descrição/referência) selecionado.
      const emModoCodigoInterno = queryDigitada.startsWith('*');
      const query = emModoCodigoInterno ? queryDigitada.slice(1).trim() : queryDigitada;
      const filtroEfetivo = emModoCodigoInterno ? 'codinterno' : filtro;
      // 'contem' fixo no modo "*código", mesmo motivo do useEffect acima.
      const modoBuscaEfetivo = emModoCodigoInterno ? 'contem' : modoBusca;
      if (!query) return;

      carregandoMaisRef.current = true;
      const requestId = requestIdRef.current;
      const proximaPagina = pagina + 1;

      ProdutosService.getAll(proximaPagina, query, filtroEfetivo, TAMANHO_PAGINA, modoBuscaEfetivo)
        .then((result) => {
          carregandoMaisRef.current = false;
          if (requestId !== requestIdRef.current) return; // busca mudou nesse meio tempo
          if (result instanceof Error) {
            console.error(result.message);
            return;
          }

          setOpcoes((prev) => [...prev, ...result.data.map(mapProduto)]);
          setPagina(proximaPagina);
        })
        .catch(() => {
          carregandoMaisRef.current = false;
        });
    };

    // Opção destacada no momento (seta pra cima/baixo OU hover do mouse) —
    // usada no Enter (ver onKeyDown mais abaixo) pra selecionar de verdade
    // o item que a listagem trouxe, em vez de reinterpretar o texto digitado
    // (ex.: "02") como se fosse um código de barras.
    const highlightedOptionRef = useRef<TAutoCompleteOption | null>(null);

    // Busca por código (Enter) em andamento — um segundo Enter nesse meio
    // tempo (operador apertando de novo, ou o leitor mandando dois Enter) é
    // ignorado, senão o mesmo produto era lançado duas vezes.
    const buscandoCodigoRef = useRef(false);

    // Texto que fica no campo depois de lançar um produto por código: no
    // modo rápido (limparAoSelecionar) o campo SEMPRE volta vazio — antes
    // ficava o nome do produto, e um novo Enter casava esse nome com a
    // listagem e lançava o mesmo item outra vez. Fora do modo rápido o nome
    // continua escrito até a próxima pesquisa, como sempre foi.
    const textoAposLancarPorCodigo = (descricao: string) => (limparAoSelecionar ? '' : descricao);
    const limparListagemAposLancar = () => {
      if (!limparAoSelecionar) return;
      setOpcoes([]);
      setTotalCount(0);
      highlightedOptionRef.current = null;
    };

    const handleHighlightChange = (
      _event: React.SyntheticEvent,
      option: TAutoCompleteOption | null,
      reason: string
    ) => {
      highlightedOptionRef.current = option?._footer ? null : option;
      if (reason !== 'keyboard' || !option || ultimaSetaRef.current !== 'down') return;
      const indiceAtual = opcoes.findIndex((opcao) => opcao.idprod === option.idprod);
      if (indiceAtual === opcoes.length - 1) {
        carregarProximaPagina();
      }
    };

    const handleInputChange = (event: React.SyntheticEvent, newValue: string) => {
      if (onInputChange) onInputChange(event, newValue);
      else setInternalInputValue(newValue);
      if (!newValue) {
        setInternalValue(null);
        setOpcoes([]);
      }
    };

    const handleChange = (event: React.SyntheticEvent, newValue: TAutoCompleteOption | null) => {
      // Linha de rodapé "todos os itens carregados" (ver FOOTER_TODOS_CARREGADOS)
      // não é um produto de verdade — nunca deve virar seleção/ir pro carrinho,
      // mesmo que o operador consiga chegar nela via teclado ou clique.
      if (newValue?._footer) return;
      if (onChange) onChange(newValue);

      if (newValue) {
        // 🔥 IMPORTANTE: Chamar onSelect para adicionar ao carrinho
        onSelect({
          idprod: newValue.idproduto,
          descricao: newValue.descricao,
          precovenda: newValue.precovenda,
          un: newValue.un,
          referencia: newValue.referencia,
          marca: newValue.marca,
        });

        // `internalValue` sempre vai pra null (não pra `newValue`) pra
        // evitar o Autocomplete reclamar de "value" que não bate com
        // nenhuma opção assim que a próxima busca trouxer uma lista
        // diferente.
        setInternalValue(null);
        setOpcoes([]);
        marcarSelecaoRecente();

        // limparAoSelecionar (modo rápido, ver prop) — campo limpo na hora.
        // Padrão: mantém o nome do produto escrito até o operador começar a
        // digitar uma pesquisa nova (a pedido, modo edição).
        const textoFinal = limparAoSelecionar ? '' : newValue.label;
        if (!isControlled) {
          setInternalInputValue(textoFinal);
        } else if (onInputChange) {
          onInputChange(event, textoFinal);
        }
      }
    };

    // Some ao fim da lista só depois de carregar tudo que a busca tem
    // (opcoes.length >= totalCount) — enquanto ainda houver mais páginas,
    // chegar no último item carregado deve continuar buscando (ver
    // handleHighlightChange/carregarProximaPagina), não mostrar esse aviso.
    // De propósito NÃO usa `getOptionDisabled`: o MUI pula opção desabilitada
    // indo pra a PRÓXIMA disponível com wrap incondicional (ver
    // validOptionIndex em useAutocomplete.js), o que faria a seta pra baixo
    // aqui voltar pro primeiro item — exatamente o bug que essa linha existe
    // pra evitar. Em vez disso, o rodapé é uma opção normal (navegável), só
    // que handleChange (acima) ignora a seleção dela.
    const opcoesExibidas = opcoes.length > 0 && opcoes.length >= totalCount
      ? [...opcoes, FOOTER_TODOS_CARREGADOS]
      : opcoes;

    return (
      <Autocomplete
        loading={isLoading}
        disabled={isExternalLoading || disabled}
        options={opcoesExibidas}
        // Sem isso, o MUI reaplica o próprio filtro de texto por cima das
        // opções (que a gente já filtrou no servidor), comparando o
        // `inputValue` digitado com o label de cada opção — e no modo
        // "*código" o campo mostra o "*" junto (ex.: "*56"), que nunca
        // aparece literalmente em nenhum label, então o MUI removia TODAS
        // as opções antes de renderizar (a lista abria vazia, mesmo com a
        // busca no servidor tendo funcionado). `options` já vem pronto do
        // backend — não precisa (e não deve) filtrar de novo aqui.
        filterOptions={(options) => options}
        value={internalValue}
        inputValue={currentInputValue}
        onInputChange={handleInputChange}
        onChange={handleChange}
        onHighlightChange={handleHighlightChange}
        // Sem isso, seta pra cima no primeiro item "voltava" pro último
        // (wrap do MUI) — além de disparar busca de mais produtos indevida
        // (já corrigido via ultimaSetaRef), também não era o comportamento
        // esperado: no primeiro item, seta pra cima deve ficar parada ali.
        disableListWrap
        isOptionEqualToValue={(option, val) => option.idprod === val.idprod}
        // Sem ícone fixo no fim do campo (era um StorefrontIcon, a pedido) —
        // só o spinner de carregamento quando `isLoading`, mais nada.
        popupIcon={isLoading ? <CircularProgress size={26} sx={{ color: '#fff' }} /> : null}
        noOptionsText={null}
        open={opcoes.length > 0 && currentInputValue?.length > 0}
        onClose={() => {}}
        // Código interno, descrição/referência e preço de venda em colunas
        // próprias (referência só aparece se o produto tiver — em qualquer
        // modo de busca, não só quando `filtro` é 'referencia') — sem isso, o
        // option default do MUI jogava tudo numa string só (`label`), com o
        // código interno grudado na frente da descrição e sem preço nenhum.
        renderOption={(props, option) => (
          option._footer ? (
            <li {...props} key={option.idprod} style={{ pointerEvents: 'none' }}>
              <Typography
                variant="caption"
                align="center"
                sx={{ width: '100%', opacity: 0.6, fontStyle: 'italic', py: 0.25 }}
              >
                Todos os {totalCount} produto{totalCount === 1 ? '' : 's'} encontrados foram exibidos
              </Typography>
            </li>
          ) : (
          <li {...props} key={option.idprod}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1.5 }}>
              {!!option.codinterno && (
                <Typography noWrap sx={{ flex: '0 0 auto', minWidth: 40, opacity: 0.6, fontSize: '0.85rem' }}>
                  {option.codinterno}
                </Typography>
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                  {option.descricao}
                </Typography>
                {!!option.referencia && (
                  <Typography noWrap variant="caption" sx={{ opacity: 0.7, display: 'block' }}>
                    Ref: {option.referencia}
                  </Typography>
                )}
              </Box>
              {/* minWidth igual ao da coluna de Valor Unitário na barra de
                  lançamento (ver Pdv.tsx) — faz o preço aqui cair alinhado
                  com a mesma largura de referência visual. */}
              <Typography noWrap sx={{ flex: '0 0 auto', minWidth: 84, textAlign: 'right', fontWeight: 700, fontSize: '0.9rem' }}>
                {option.precovenda.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </Typography>
            </Box>
          </li>
          )
        )}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              background: theme.dropdownBackground,
              backdropFilter: 'blur(10px)',
              borderRadius: '0 0 8px 8px',
              border: `1px solid ${theme.borderColor}`,
              boxShadow: '0 12px 30px rgba(0,0,0,0.6)',
              color: theme.dropdownTextColor ?? theme.textColor,
            },
          },
        }}
        // Scroll da listagem seguindo as cores do tema — sem isso, a barra de
        // rolagem ficava com o estilo padrão do navegador (cinza/branca),
        // destoando do dropdown colorido por trás.
        ListboxProps={{
          sx: {
            scrollbarColor: `${theme.borderColor} transparent`,
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': { width: 8 },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.borderColor,
              borderRadius: 8,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: theme.dropdownHover,
            },
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            onKeyDown={async (event) => {
              if (event.key === 'ArrowUp') { ultimaSetaRef.current = 'up'; return; }
              if (event.key === 'ArrowDown') { ultimaSetaRef.current = 'down'; return; }
              if (event.key !== 'Enter') return;

              const value = currentInputValue?.trim();

              if (!value) return;

              if (buscandoCodigoRef.current) {
                event.preventDefault();
                event.stopPropagation();
                onBarcodeBusy?.();
                return;
              }

              // 🔥 Item destacado na listagem (seta pra cima/baixo ou hover)
              // — Enter seleciona ELE, igual um clique. Sem essa checagem
              // primeiro, o texto digitado (ex.: "02", que bate no teste de
              // código de barras) era reinterpretado como código em vez de
              // pegar o produto que a busca já tinha trazido e destacado.
              const destacado = highlightedOptionRef.current;
              if (destacado) {
                event.preventDefault();
                event.stopPropagation();
                handleChange(event, destacado);
                highlightedOptionRef.current = null;
                return;
              }

              // 🔥 Remover item do carrinho pelo número (-3 remove o item nº 3)
              const removeMatch = value.match(/^-(\d+)$/);
              if (removeMatch) {
                event.preventDefault();
                event.stopPropagation();

                onRemoveByNumber?.(Number(removeMatch[1]));

                if (!isControlled) {
                  setInternalInputValue('');
                } else if (onInputChange) {
                  onInputChange(event, '');
                }
                return;
              }

              // 🔥 Quantidade (+5) — "*" agora é reservado pra busca por
              // código interno (ver useEffect acima), então a quantidade
              // mudou de prefixo pra "+", sem mais conflito entre os dois.
              if (value.startsWith('+')) {
                event.preventDefault();
                event.stopPropagation();

                const match = value.match(/^\+(\d+(?:[.,]\d{1,2})?)$/);

                if (!match) return;

                const quantidade = Number(match[1].replace(',', '.'));

                if (quantidade > 0 && onQuantityDetected) {
                  onQuantityDetected(Number(quantidade.toFixed(2)));
                }

                if (!isControlled) {
                  setInternalInputValue('');
                } else if (onInputChange) {
                  onInputChange(event, '');
                }

                return;
              }
            
              // 🔥 Código de barras
              const isCodigo = /^\d+$/.test(value) && !value.startsWith('00');
            
              if (isCodigo && autoSearchOnEnter) {
                event.preventDefault();
                event.stopPropagation();

                setIsLoading(true);
                buscandoCodigoRef.current = true;
                // Nome do produto lido, se encontrado — fica escrito no
                // campo até a próxima pesquisa (a pedido), em vez de limpar;
                // só limpa mesmo quando o código não é encontrado (nada
                // útil pra mostrar). No modo rápido sempre limpa (ver
                // textoAposLancarPorCodigo).
                let descricaoEncontrada = '';
                try {
                  const result = await buscarProdutoPorCodigo(value);

                  if (result) {
                    descricaoEncontrada = result.descricao || '';
                    if (onBarcodeFound) {
                      onBarcodeFound(result);
                    } else if (onBarcodeEnter) {
                      onBarcodeEnter(value);
                    }
                  } else {
                    onBarcodeNotFound?.(value);
                  }
                } catch (error) {
                  console.error('Erro ao buscar código de barras:', error);
                } finally {
                  setIsLoading(false);
                  buscandoCodigoRef.current = false;
                }

                marcarSelecaoRecente();
                limparListagemAposLancar();
                if (!isControlled) {
                  setInternalInputValue(textoAposLancarPorCodigo(descricaoEncontrada));
                } else if (onInputChange) {
                  onInputChange(event, textoAposLancarPorCodigo(descricaoEncontrada));
                }
                return;
              }
            
              // 🔥 Formato quantidade,codigo (ex: 2,789123)
              if (value.includes(',')) {
                event.preventDefault();
                event.stopPropagation();
                
                const [qtdPart, codigoPart] = value.split(',');
                const quantidade = parseFloat(qtdPart.replace(',', '.'));

                // Nome do produto lido, se encontrado — mesmo critério do
                // bloco de código de barras acima: fica escrito até a
                // próxima pesquisa, só limpa se não encontrar nada.
                let descricaoEncontrada = '';
                if (!isNaN(quantidade) && quantidade > 0 && codigoPart) {
                  // Primeiro define a quantidade
                  if (onQuantityDetected) {
                    onQuantityDetected(quantidade);
                  }

                  // Depois busca o código
                  setIsLoading(true);
                  buscandoCodigoRef.current = true;
                  try {
                    const result = await buscarProdutoPorCodigo(codigoPart);

                    if (result) {
                      descricaoEncontrada = result.descricao || '';
                      if (onBarcodeFound) {
                        onBarcodeFound(result);
                      } else if (onBarcodeEnter) {
                        onBarcodeEnter(codigoPart);
                      }
                    } else {
                      onBarcodeNotFound?.(codigoPart);
                    }
                  } catch (error) {
                    console.error('Erro ao buscar código de barras:', error);
                  } finally {
                    setIsLoading(false);
                    buscandoCodigoRef.current = false;
                  }
                }

                marcarSelecaoRecente();
                limparListagemAposLancar();
                if (!isControlled) {
                  setInternalInputValue(textoAposLancarPorCodigo(descricaoEncontrada));
                } else if (onInputChange) {
                  onInputChange(event, textoAposLancarPorCodigo(descricaoEncontrada));
                }
                return;
              }
            
              // 🔥 Para buscas normais por texto (descrição)
              // Verifica se tem exatamente uma opção e é igual ao que foi digitado
              const bateComOpcao = (opt: TAutoCompleteOption) =>
                opt.label.toLowerCase() === value.toLowerCase() ||
                opt.descricao.toLowerCase() === value.toLowerCase();

              if (opcoes.length === 1 && bateComOpcao(opcoes[0])) {
                event.preventDefault();
                // Seleciona automaticamente a única opção
                handleChange(event, opcoes[0]);
                return;
              }

              // Se tiver opções disponíveis e o valor corresponder a alguma
              if (opcoes.length > 0) {
                const exactMatch = opcoes.find(bateComOpcao);
                if (exactMatch) {
                  event.preventDefault();
                  handleChange(event, exactMatch);
                  return;
                }
              }
              
              // Se não encontrou correspondência exata e é texto puro (não é código)
              // Mostrar mensagem ou apenas limpar
              if (!isCodigo && value.length > 0) {
                // Opcional: mostrar mensagem que produto não foi encontrado
                console.log('Nenhum produto encontrado para:', value);
                
                // Limpa o campo após um pequeno delay
                setTimeout(() => {
                  if (!isControlled) {
                    setInternalInputValue('');
                  } else if (onInputChange) {
                    onInputChange(event, '');
                  }
                }, 100);
              }
            }}
            inputRef={inputRef}
            placeholder={placeholder}
            variant="outlined"
            sx={
              variant === 'bar'
                ? {
                    '& .MuiOutlinedInput-root': {
                      background: 'transparent',
                      color: theme.textColor,
                      p: '0 !important',
                      fontSize: pdvScale(38),
                      fontWeight: 600,
                      borderRadius: 0,

                      '& fieldset': { border: 'none' },

                      // .MuiOutlinedInput-input (não só "& input") — o
                      // seletor genérico por tag perde em especificidade pro
                      // padding default do MUI (.MuiOutlinedInput-root
                      // .MuiOutlinedInput-input, duas classes), que senão
                      // continua valendo (16.5px fixo, sem escalar com
                      // --pdv-scale) por cima do nosso.
                      '& .MuiOutlinedInput-input': {
                        padding: `${pdvScale(4)} 0 !important`,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      },

                      '& input::placeholder': {
                        color: theme.placeholderColor,
                        opacity: 1,
                      },
                    },
                  }
                : {
                    '& .MuiOutlinedInput-root': {
                      background: theme.inputBackground,
                      color: theme.textColor,
                      height: { xs: 48, sm: 54, md: 60, lg: 68, xl: 75 },
                      fontSize: { xs: '0.85rem', sm: '0.92rem', md: '1rem', lg: '1.05rem', xl: '1.1rem' },
                      borderRadius: '16px 16px 0 0',
                      transition: 'box-shadow .15s, border-color .15s',

                      '& fieldset': {
                        border: `1px solid ${theme.borderColor}`,
                        borderRadius: '16px 16px 0 0',
                      },

                      '& input::placeholder': {
                        color: theme.placeholderColor,
                      },
                    },
                  }
            }
          />
        )}
        sx={{
          width: '100%',
          '& .MuiAutocomplete-option': {
            '&:hover, &.Mui-focused': {
              backgroundColor: theme.dropdownHover,
            },
          }
        }}
      />
    );
  }
);

AutoCompleteProdutopdv.displayName = 'AutoCompleteProdutopdv';